/**
 * Webhook event catalog (code-authoritative) — the single source of truth for
 * every event a webhook definition can subscribe to. Shared between apps/web
 * (the event picker) and apps/api (subscription validation + dispatch matching).
 *
 * Two families of events exist:
 *
 *   • ACTION events — instantaneous facts emitted the moment a mutation is
 *     recorded. Their `key` is IDENTICAL to the `audit_events.action` (or
 *     `login_audit.event`) string the platform already writes, so the webhook
 *     dispatcher can match a published domain event to a subscription by key
 *     with no translation. Adding a module audit action here makes it webhookable.
 *
 *   • CONDITION events — derived, edge-triggered facts computed on each periodic
 *     data refresh by comparing the new snapshot against the previous one (a
 *     Jenkins job that turned red, a server that went offline, a disk that
 *     crossed its threshold). These have their own keys (never audit actions)
 *     and some carry a per-webhook numeric `threshold` (see WebhookThresholds).
 */

/** Which resource a threshold-bearing condition event compares against. */
export type WebhookThresholdResource = "disk" | "cpu" | "memory";

/** A single subscribable event. */
export interface WebhookEventDef {
  /** Stable subscription key (equals the audit action for ACTION events). */
  key: string;
  /** Grouping shown in the picker (mirrors the audit-log categories). */
  category: WebhookEventCategory;
  /** Human-readable label for the picker. */
  label: string;
  /** ACTION = audit-layer fact; CONDITION = derived, edge-triggered. */
  family: "action" | "condition";
  /**
   * For CONDITION events driven by a numeric threshold, which resource the
   * per-webhook threshold applies to. Undefined for all other events.
   */
  threshold?: WebhookThresholdResource;
  /**
   * The platform no longer emits this event, but the key REMAINS VALID so that
   * webhook definitions already subscribed to it keep validating and keep their
   * stored `events[]` intact. Retired events are hidden from the event picker,
   * so nobody can newly subscribe to something that will never fire.
   *
   * Use this instead of deleting an entry: removing a key makes every stored
   * subscription containing it fail validation on the next save.
   */
  retired?: true;
}

export type WebhookEventCategory =
  | "customer"
  | "server"
  | "container"
  | "jenkins"
  | "report"
  | "service"
  | "m365"
  | "settings"
  | "browser"
  | "webhook"
  | "fortigate"
  | "api_key"
  | "copilot"
  | "auth";

/** Human labels for each category (drives the grouped picker headings). */
export const WEBHOOK_EVENT_CATEGORY_LABELS: Record<
  WebhookEventCategory,
  string
> = {
  customer: "Customers",
  server: "Servers",
  container: "Containers",
  jenkins: "Jenkins",
  report: "Reports",
  service: "Services catalog",
  m365: "Microsoft 365",
  settings: "Settings",
  browser: "Browser sessions",
  webhook: "Webhooks",
  fortigate: "FortiGate",
  api_key: "API keys",
  copilot: "Copilot integrations",
  auth: "Authentication",
};

/**
 * The full catalog. Order within a category is the display order. Keep ACTION
 * keys in lock-step with the module `*_AUDIT_ACTIONS` (and `login_audit.event`).
 */
export const WEBHOOK_EVENTS: readonly WebhookEventDef[] = [
  // ── Customers ──────────────────────────────────────────────────────────────
  { key: "customer_upserted", category: "customer", label: "Customer created/updated", family: "action" },
  { key: "customer_bulk_upserted", category: "customer", label: "Customers bulk imported", family: "action" },
  { key: "customer_result_emailed", category: "customer", label: "Provisioning result emailed", family: "action" },
  { key: "customer_migrated", category: "customer", label: "Customer migrated", family: "action" },

  // ── Servers (action) ─────────────────────────────────────────────────────
  { key: "server_created", category: "server", label: "Server added", family: "action" },
  { key: "server_updated", category: "server", label: "Server updated", family: "action" },
  { key: "server_deleted", category: "server", label: "Server deleted", family: "action" },
  { key: "server_tag_renamed", category: "server", label: "Server tag renamed", family: "action" },
  { key: "server_tag_deleted", category: "server", label: "Server tag deleted", family: "action" },
  { key: "server_refreshed", category: "server", label: "Server refreshed (WUD)", family: "action" },
  { key: "server_pulseway_refreshed", category: "server", label: "Pulseway data refreshed", family: "action" },
  { key: "server_pulseway_script_ran", category: "server", label: "Pulseway update & security fields refreshed", family: "action" },
  { key: "server_migrated", category: "server", label: "Server migrated", family: "action" },
  // ── Servers (condition) ────────────────────────────────────────────────────
  { key: "server_offline", category: "server", label: "Server went OFFLINE", family: "condition" },
  { key: "server_online", category: "server", label: "Server came back ONLINE", family: "condition" },
  { key: "server_disk_exceeded", category: "server", label: "Disk usage crossed threshold", family: "condition", threshold: "disk" },
  { key: "server_cpu_exceeded", category: "server", label: "CPU usage crossed threshold", family: "condition", threshold: "cpu" },
  { key: "server_memory_exceeded", category: "server", label: "Memory usage crossed threshold", family: "condition", threshold: "memory" },

  // ── Containers ───────────────────────────────────────────────────────────
  { key: "container_refreshed", category: "container", label: "Containers refreshed", family: "action" },
  { key: "container_update_tag", category: "container", label: "Container tag update available", family: "condition" },
  { key: "container_update_digest", category: "container", label: "Container digest update available", family: "condition" },
  { key: "container_update_unknown", category: "container", label: "Container update available (unknown kind)", family: "condition" },

  // ── Jenkins ────────────────────────────────────────────────────────────────
  { key: "jenkins_update_triggered", category: "jenkins", label: "Manual Jenkins sync triggered", family: "action" },
  { key: "jenkins_build_triggered", category: "jenkins", label: "Jenkins build triggered", family: "action" },
  { key: "jenkins_job_failed", category: "jenkins", label: "Job build FAILED", family: "condition" },
  { key: "jenkins_job_recovered", category: "jenkins", label: "Job recovered (failure → success)", family: "condition" },
  { key: "jenkins_job_unstable", category: "jenkins", label: "Job became UNSTABLE", family: "condition" },

  // ── Reports ──────────────────────────────────────────────────────────────
  { key: "report_generated", category: "report", label: "Report generated", family: "action" },
  { key: "report_template_saved", category: "report", label: "Report template saved", family: "action" },
  { key: "report_template_deleted", category: "report", label: "Report template deleted", family: "action" },

  // ── Services catalog ─────────────────────────────────────────────────────
  { key: "service_created", category: "service", label: "Service created", family: "action" },
  { key: "service_updated", category: "service", label: "Service updated", family: "action" },
  { key: "service_deleted", category: "service", label: "Service deleted", family: "action" },

  // ── Microsoft 365 ──────────────────────────────────────────────────────────
  { key: "m365_provision_requested", category: "m365", label: "M365 provisioning requested", family: "action" },
  { key: "m365_provision_started", category: "m365", label: "M365 provisioning started", family: "action" },
  { key: "m365_provision_completed", category: "m365", label: "M365 provisioning completed", family: "action" },
  { key: "m365_provision_failed", category: "m365", label: "M365 provisioning FAILED", family: "action" },
  { key: "m365_hudu_saved", category: "m365", label: "M365 integration saved to Hudu", family: "action" },

  // ── FortiGate ────────────────────────────────────────────────────────────
  // New events, so they follow the <domain>.<action> convention. The pre-existing
  // snake_case keys above are grandfathered (renaming one would orphan every
  // subscription to it), not a precedent.
  { key: "fortigate.note.saved", category: "fortigate", label: "Device note saved", family: "action" },
  { key: "fortigate.note.deleted", category: "fortigate", label: "Device note cleared", family: "action" },
  { key: "fortigate.schedule.updated", category: "fortigate", label: "Monitor settings changed", family: "action" },
  { key: "fortigate.api.checked", category: "fortigate", label: "API check run against a device", family: "action" },
  { key: "fortigate.report.sent", category: "fortigate", label: "Monitor report sent", family: "action" },
  { key: "fortigate.sweep.started", category: "fortigate", label: "Fleet sweep started", family: "action" },

  // ── Copilot business integrations (global MCP catalog) ───────────
  // Subscribable because a change here alters what data can be placed in front
  // of a language model for EVERY user of the platform. A SIEM that wants one
  // signal from this feature should take `secret_replaced`, `endpoint_changed`
  // and `auth_changed` — the three that describe a change of trust rather than
  // a change of wording. No payload carries a secret value; `secret_replaced`
  // says which credential was replaced and nothing more.
  { key: "copilot.mcp_integration.created", category: "copilot", label: "Business integration created", family: "action" },
  { key: "copilot.mcp_integration.updated", category: "copilot", label: "Business integration updated", family: "action" },
  { key: "copilot.mcp_integration.endpoint_changed", category: "copilot", label: "Business integration endpoint changed", family: "action" },
  { key: "copilot.mcp_integration.auth_changed", category: "copilot", label: "Business integration authentication changed", family: "action" },
  { key: "copilot.mcp_integration.client_id_changed", category: "copilot", label: "Business integration OAuth client id changed", family: "action" },
  { key: "copilot.mcp_integration.oauth_client_registered", category: "copilot", label: "Business integration OAuth client registered with provider", family: "action" },
  { key: "copilot.mcp_integration.secret_replaced", category: "copilot", label: "Business integration credential replaced", family: "action" },
  { key: "copilot.mcp_integration.allowed_tools_changed", category: "copilot", label: "Business integration allowed tools changed", family: "action" },
  { key: "copilot.mcp_integration.enabled", category: "copilot", label: "Business integration enabled", family: "action" },
  { key: "copilot.mcp_integration.disabled", category: "copilot", label: "Business integration disabled", family: "action" },
  { key: "copilot.mcp_integration.deleted", category: "copilot", label: "Business integration deleted", family: "action" },

  // ── API keys ─────────────────────────────────────────────────────────────
  { key: "api_key.created", category: "api_key", label: "API key created", family: "action" },
  { key: "api_key.revoked", category: "api_key", label: "API key revoked", family: "action" },
  { key: "api_key.rotated", category: "api_key", label: "API key rotated", family: "action" },
  { key: "api_key.updated", category: "api_key", label: "API key updated", family: "action" },

  // ── Settings ─────────────────────────────────────────────────────────────
  { key: "settings_general_updated", category: "settings", label: "General settings updated", family: "action" },
  { key: "settings_branding_updated", category: "settings", label: "Deployment branding updated", family: "action" },
  // Retired in favour of api_key.created / api_key.revoked above. These two were
  // advertised in this catalog but NO CODE EVER EMITTED THEM, so anything
  // subscribed to them has been silently dead since they were added. The keys
  // stay valid so those definitions keep saving; the picker offers the real
  // events instead.
  { key: "settings_api_key_created", category: "settings", label: "API key created (retired)", family: "action", retired: true },
  { key: "settings_api_key_revoked", category: "settings", label: "API key revoked (retired)", family: "action", retired: true },

  // ── Browser sessions ─────────────────────────────────────────────────────
  { key: "browser_launch_request_created", category: "browser", label: "Browser launch requested", family: "action" },
  // Retired: viewing a session is a READ, and the detail endpoint is polled
  // while a session launches, so this fired repeatedly for one launch. The audit
  // write is gone; the key stays valid for any definition still subscribed.
  { key: "browser_session_viewed", category: "browser", label: "Browser session viewed", family: "action", retired: true },
  { key: "browser_launch_consumed", category: "browser", label: "Browser launch consumed", family: "action" },
  { key: "browser_agent_active", category: "browser", label: "Browser agent active", family: "action" },
  { key: "browser_agent_closed", category: "browser", label: "Browser agent closed", family: "action" },
  { key: "browser_agent_failed", category: "browser", label: "Browser agent FAILED", family: "action" },
  { key: "browser_session_close_requested", category: "browser", label: "Browser session close requested", family: "action" },
  { key: "browser.session_relaunched", category: "browser", label: "Browser session reopened", family: "action" },
  { key: "browser.partnership_requested", category: "browser", label: "Reseller invitation opened", family: "action" },

  // ── Webhooks (meta) ──────────────────────────────────────────────────────
  { key: "webhook_created", category: "webhook", label: "Webhook created", family: "action" },
  { key: "webhook_updated", category: "webhook", label: "Webhook updated", family: "action" },
  { key: "webhook_deleted", category: "webhook", label: "Webhook deleted", family: "action" },
  { key: "webhook_enabled", category: "webhook", label: "Webhook enabled", family: "action" },
  { key: "webhook_disabled", category: "webhook", label: "Webhook disabled", family: "action" },
  { key: "webhook_tested", category: "webhook", label: "Webhook tested", family: "action" },
  { key: "webhook_replayed", category: "webhook", label: "Webhook delivery replayed", family: "action" },

  // ── Authentication (login_audit) ───────────────────────────────────────────
  { key: "login_success", category: "auth", label: "Login succeeded", family: "action" },
  { key: "login_failure", category: "auth", label: "Login FAILED", family: "action" },
  { key: "lockout", category: "auth", label: "Account locked out", family: "action" },
  { key: "logout", category: "auth", label: "Logout", family: "action" },
  { key: "refresh_reuse", category: "auth", label: "Refresh-token reuse detected", family: "action" },
];

/**
 * Every valid subscription key (used by validation + fast membership checks).
 * Includes retired keys on purpose — a definition already subscribed to one
 * must keep saving successfully.
 */
export const WEBHOOK_EVENT_KEYS: readonly string[] = WEBHOOK_EVENTS.map(
  (e) => e.key,
);

/** The events a user may newly subscribe to — what the picker should offer. */
export const SUBSCRIBABLE_WEBHOOK_EVENTS: readonly WebhookEventDef[] =
  WEBHOOK_EVENTS.filter((e) => !e.retired);

const EVENT_BY_KEY = new Map(WEBHOOK_EVENTS.map((e) => [e.key, e]));

/** Look up an event definition by key (undefined for unknown keys). */
export function webhookEventByKey(key: string): WebhookEventDef | undefined {
  return EVENT_BY_KEY.get(key);
}

/** True when the key names a real, subscribable event. */
export function isWebhookEventKey(key: string): boolean {
  return EVENT_BY_KEY.has(key);
}

/** Per-webhook numeric thresholds (percent) for threshold-bearing conditions. */
export interface WebhookThresholds {
  /** Fire server_disk_exceeded when any disk's used% strictly exceeds this. */
  disk: number;
  /** Fire server_cpu_exceeded when CPU% strictly exceeds this. */
  cpu: number;
  /** Fire server_memory_exceeded when memory% strictly exceeds this. */
  memory: number;
}

/** Dashboard-matching default: CPU/RAM/Disk warn above 75% used. */
export const DEFAULT_WEBHOOK_THRESHOLDS: WebhookThresholds = {
  disk: 75,
  cpu: 75,
  memory: 75,
};
